import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Collapse, Dropdown, Form, Tabs, Tab } from 'react-bootstrap';
import apiClient from '../api/axios';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { toast } from 'react-toastify';
import EventPlanItemModal from '../components/EventPlanItemModal';
import ConfirmationModal from '../components/ConfirmationModal';
import AddParticipantModal from '../components/AddParticipantModal';
import { 
    DndContext, 
    closestCenter, 
    useSensor, 
    useSensors, 
    MouseSensor, 
    TouchSensor 
} from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import SortableEventPlanItem from '../components/SortableEventPlanItem';
import PlaylistPlayerModal from '../components/PlaylistPlayerModal';
import AddRehearsalModal from '../components/AddRehearsalModal';
import EditEventModal from '../components/EditEventModal';
import ScaleModal from '../components/ScaleModal';
import { formatUtcToLocal, formatUtcToLocalWithWeekday } from '../utils/dateUtils';

const CustomToggle = React.forwardRef(({ children, onClick }, ref) => (
    <a href="" ref={ref} onClick={e => { e.preventDefault(); onClick(e); }} className="text-dark p-3">{children}</a>
));

const EventPage = () => {
    const { eventId } = useParams();
    const [event, setEvent] = useState(null);
    const [users, setUsers] = useState([]);
    
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const { isAuthenticated, user } = useAuth();
    const { canUpdate, loading: permissionsLoading } = usePermissions('event');

    const [eventRoles, setEventRoles] = useState([]);
    const [isTeamCollapsed, setIsTeamCollapsed] = useState(true);
    const [showParticipantModal, setShowParticipantModal] = useState(false);
    const [showItemModal, setShowItemModal] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
    const [itemToDeleteId, setItemToDeleteId] = useState(null);
    const [showConfirmDeleteParticipantModal, setShowConfirmDeleteParticipantModal] = useState(false);
    const [participantToDeleteId, setParticipantToDeleteId] = useState(null);
    const [showPlayerModal, setShowPlayerModal] = useState(false);
    const [isRehearsalsCollapsed, setIsRehearsalsCollapsed] = useState(true);
    const [showAddRehearsalModal, setShowAddRehearsalModal] = useState(false);
    const [rehearsalToDeleteId, setRehearsalToDeleteId] = useState(null);
    const [showConfirmDeleteRehearsalModal, setShowConfirmDeleteRehearsalModal] = useState(false);
    const [itemModalType, setItemModalType] = useState('item');
    const [activeTab, setActiveTab] = useState('program');

    const [showEditModal, setShowEditModal] = useState(false);
    const [showScaleModal, setShowScaleModal] = useState(false);

    const sensors = useSensors(
        useSensor(MouseSensor, { activationConstraint: { distance: 10 } }),
        useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 5 } })
    );

    const handleApiError = (err, defaultMessage) => {
        const detail = err.response?.data?.detail;
        const message = Array.isArray(detail) ? detail.map(e => `${e.loc.join(' -> ')}: ${e.msg}`).join('; ') : detail;
        setError(message || defaultMessage);
    };

    const fetchEventData = useCallback(() => {
        if (isAuthenticated && user?.organization_id) {
            setLoading(true);
            Promise.all([
                apiClient.get(`/events/${eventId}`),
                apiClient.get(`/users/`),
                apiClient.get('/event-roles/')
            ]).then(([eventResponse, usersResponse, eventRolesResponse]) => {
                setEvent(eventResponse.data);
                setUsers(usersResponse.data);
                setEventRoles(eventRolesResponse.data);
            }).catch(err => {
                handleApiError(err, 'Ошибка загрузки данных');
            }).finally(() => {
                setLoading(false);
            });
        } else if (isAuthenticated) {
            setLoading(false);
        } else {
            setLoading(false);
            setError('Вам нужно войти в систему, чтобы просматривать эту страницу.');
        }
    }, [eventId, isAuthenticated, user?.organization_id]);

    useEffect(() => {
        fetchEventData();
    }, [fetchEventData]);

    const handleToggleTemplate = async () => {
        if (!event) return;
        const newIsTemplate = !event.is_template;
        try {
            await apiClient.put(`/events/${event.id}`, { is_template: newIsTemplate });
            toast.success(newIsTemplate ? 'Событие отмечено как шаблон.' : 'Событие больше не является шаблоном.');
            fetchEventData();
        } catch (err) {
            toast.error('Не удалось обновить статус шаблона.');
        }
    };

    const handleSaveEvent = async (updatedData) => {
        if (!event) return;
        try {
            await apiClient.put(`/events/${event.id}`, updatedData);
            toast.success('Событие успешно обновлено!');
            setShowEditModal(false);
            fetchEventData();
        } catch (err) {
            handleApiError(err, 'Ошибка при обновлении события.');
            toast.error('Ошибка при обновлении события.');
        }
    };

    const handleOpenNewItemModal = (type) => { setItemModalType(type); setEditingItem(null); setShowItemModal(true); };
    const handleOpenEditItemModal = (item) => { setEditingItem(item); setShowItemModal(true); };
    const handleCloseItemModal = () => { setShowItemModal(false); setEditingItem(null); };

    const handleSaveItem = (formData) => {
        setError('');
        const request = editingItem
            ? apiClient.put(`/plan_items/${editingItem.id}`, formData)
            : apiClient.post(`/events/${eventId}/plan_items/`, formData);

        request.then((response) => {
            const savedItem = response.data;
            setEvent(prevEvent => {
                let newPlanItems;
                if (editingItem) {
                    newPlanItems = prevEvent.plan_items.map(item => 
                        item.id === savedItem.id ? savedItem : item
                    );
                } else {
                    newPlanItems = [...prevEvent.plan_items, savedItem];
                }
                return { ...prevEvent, plan_items: newPlanItems };
            });
            handleCloseItemModal();
        }).catch(err => handleApiError(err, 'Ошибка сохранения пункта плана'));
    };

    const handleConfirmDownloadPdf = async (scale) => {
        setShowScaleModal(false);
        try {
            const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            const response = await apiClient.get(`/events/${eventId}/plan/pdf?tz=${encodeURIComponent(userTimeZone)}&scale=${scale / 100}`, { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `event_${eventId}_plan.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.success('PDF успешно скачан!');
        } catch (err) {
            toast.error('Ошибка при скачивании PDF.');
        }
    };

    const handleDeleteItemClick = (itemId) => { setItemToDeleteId(itemId); setShowConfirmDeleteModal(true); };
    const handleConfirmDelete = () => {
        setError('');
        apiClient.delete(`/plan_items/${itemToDeleteId}`).then(() => {
            setEvent(prevEvent => ({
                ...prevEvent,
                plan_items: prevEvent.plan_items.filter(item => item.id !== itemToDeleteId)
            }));
            setShowConfirmDeleteModal(false);
            setItemToDeleteId(null);
        }).catch(err => {
            handleApiError(err, 'Ошибка удаления пункта');
            setShowConfirmDeleteModal(false);
        });
    };

    const handleAddParticipant = (formData) => {
        setError(''); // Очищаем старые ошибки
        apiClient.post(`/events/${eventId}/participants`, formData)
            .then(() => {
                setShowParticipantModal(false);
                fetchEventData();
                toast.success('Участник успешно добавлен!'); // Добавим уведомление об успехе
                setActiveTab('team');
            })
            .catch(err => {
                const detail = err.response?.data?.detail;
                const message = Array.isArray(detail) ? detail.map(e => e.msg).join('; ') : detail;
                toast.error(message || 'Ошибка добавления участника'); // Показываем toast с ошибкой
            });
    };

    const handleOpenAddParticipantModal = (e) => { e.stopPropagation(); setShowParticipantModal(true); };
    const handleCloseAddParticipantModal = () => setShowParticipantModal(false);

    const handleDeleteParticipantClick = (participantId) => { setParticipantToDeleteId(participantId); setShowConfirmDeleteParticipantModal(true); };
    const handleResendInvitation = async (participantId) => {
        try {
            await apiClient.post(`/events/${eventId}/participants/${participantId}/resend_invitation`);
            toast.success('Приглашение успешно отправлено!');
        } catch (err) {
            handleApiError(err, 'Ошибка при отправке приглашения');
            toast.error('Ошибка при отправке приглашения.');
        }
    };

    const handleConfirmParticipation = async (participantId) => {
        try {
            await apiClient.post(`/participants/${participantId}/confirm`);
            toast.success('Участие успешно подтверждено!');
            fetchEventData(); // Re-fetch data to update the UI
        } catch (err) {
            handleApiError(err, 'Ошибка при подтверждении участия.');
            toast.error('Ошибка при подтверждении участия.');
        }
    };

    const handleConfirmDeleteParticipant = () => {
        setError('');
        apiClient.delete(`/participants/${participantToDeleteId}`).then(() => {
            setShowConfirmDeleteParticipantModal(false);
            setParticipantToDeleteId(null);
            fetchEventData();
        }).catch(err => {
            handleApiError(err, 'Ошибка удаления участника');
            setShowConfirmDeleteParticipantModal(false);
        });
    };

    const handleOpenAddRehearsalModal = () => setShowAddRehearsalModal(true);
    const handleCloseAddRehearsalModal = () => setShowAddRehearsalModal(false);

    const handleSaveRehearsal = (rehearsalData) => {
        apiClient.post(`/events/${eventId}/rehearsals`, rehearsalData).then(() => {
            setShowAddRehearsalModal(false);
            fetchEventData();
            toast.success('Репетиция успешно добавлена!');
            setActiveTab('rehearsals');
        }).catch(err => {
            const detail = err.response?.data?.detail;
            const message = Array.isArray(detail) ? detail.map(e => `${e.loc.join(' -> ')}: ${e.msg}`).join('; ') : detail;
            toast.error(message || 'Ошибка добавления репетиции');
        });
    };

    const handleDeleteRehearsalClick = (rehearsalId) => { setRehearsalToDeleteId(rehearsalId); setShowConfirmDeleteRehearsalModal(true); };
    const handleConfirmDeleteRehearsal = () => {
        apiClient.delete(`/rehearsals/${rehearsalToDeleteId}`).then(() => {
            setShowConfirmDeleteRehearsalModal(false);
            setRehearsalToDeleteId(null);
            fetchEventData();
            toast.success('Репетиция успешно удалена!');
        }).catch(err => {
            handleApiError(err, 'Ошибка удаления репетиции');
            setShowConfirmDeleteRehearsalModal(false);
        });
    };

    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (active.id !== over.id) {
            setEvent((prevEvent) => {
                const oldIndex = prevEvent.plan_items.findIndex(item => item.id === active.id);
                const newIndex = prevEvent.plan_items.findIndex(item => item.id === over.id);
                const newPlanItems = arrayMove(prevEvent.plan_items, oldIndex, newIndex);
                const updatedPlanItems = newPlanItems.map((item, index) => ({ ...item, order: index }));
                apiClient.post(`/events/${eventId}/plan_items/reorder`, { item_ids: updatedPlanItems.map(item => item.id) })
                    .catch(err => {
                        handleApiError(err, 'Ошибка сохранения нового порядка');
                        fetchEventData();
                    });
                return { ...prevEvent, plan_items: updatedPlanItems };
            });
        }
    };

    if (loading || permissionsLoading) return <div className="text-center">Загрузка...</div>;
    if (error) return <div className="alert alert-danger">{error}</div>;
    if (!event) return <div className="alert alert-warning">Событие не найдено.</div>;

    const formatDuration = (seconds) => {
        if (seconds === null || seconds === undefined || seconds < 0) return '-';
        if (seconds === 0) return '00:00';
        const totalMinutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        const pad = (num) => String(num).padStart(2, '0');
        return `${pad(totalMinutes)}:${pad(remainingSeconds)}`;
    };

    const formatDurationVerbose = (seconds) => {
        if (seconds === null || seconds === undefined || seconds < 0) return '0 сек.';
        if (seconds === 0) return '0 сек.';
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const remainingSeconds = seconds % 60;
        const parts = [];
        if (hours > 0) parts.push(`${hours} час.`);
        if (minutes > 0) parts.push(`${minutes} мин.`);
        if (remainingSeconds > 0 || parts.length === 0) parts.push(`${remainingSeconds} сек.`);
        return parts.join(' ');
    };

    const totalDuration = event.plan_items.reduce((acc, item) => acc + (item.duration || 0), 0);
    const responsibleSuggestions = [...new Set(event.plan_items.map(item => item.responsible).filter(Boolean))];

    const playlist = event.plan_items
        .filter(item => item.arrangement?.files?.some(f => f.file_type && f.file_type.toUpperCase() === 'MP3'))
        .map(item => ({
            songTitle: item.song ? item.song.title : 'Неизвестная песня',
            files: item.arrangement.files.filter(f => f.file_type && f.file_type.toUpperCase() === 'MP3').map(file => ({ ...file, filePath: file.file_path }))
        }));
    
    return (
        <div className="mb-4">
            <div className="card mb-4">
                <div className="card-header d-flex justify-content-between align-items-center">
                    <h1 className="card-title h3 mb-0">{event.name}</h1>
                    {canUpdate && (
                        <Dropdown>
                            <Dropdown.Toggle as={CustomToggle} id="event-actions-dropdown">
                                <i className="fas fa-ellipsis-v"></i>
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                                <Dropdown.Item onClick={() => setShowEditModal(true)}>Редактировать</Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>
                    )}
                </div>
                <div className="card-body">
                    <p className="card-text"><strong>Дата:</strong> {formatUtcToLocal(event.date)}</p>
                    {canUpdate && (
                        <Form.Check 
                            type="switch"
                            id="is-template-switch"
                            label="Использовать как шаблон"
                            checked={event.is_template}
                            onChange={handleToggleTemplate}
                        />
                    )}
                </div>
            </div>

            <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} id="event-tabs" className="mb-4">
                <Tab eventKey="program" title="Программа">
                    <div className="card">
                        <div className="card-header d-flex justify-content-between align-items-center">
                            <h2 className="h4 mb-0">Общая длительность: {formatDurationVerbose(totalDuration)}</h2>
                            <div className="d-flex align-items-center">
                                <button onClick={() => setShowScaleModal(true)} className="btn btn-outline-secondary btn-sm me-2" title="Скачать PDF"><i className="fas fa-file-pdf text-danger"></i></button>
                                <button onClick={() => setShowPlayerModal(true)} className="btn btn-outline-secondary btn-sm me-2" title="Запустить плейлист"><i className="fas fa-play"></i></button>
                                {canUpdate && (
                                    <Dropdown>
                                        <Dropdown.Toggle variant="primary" size="sm" id="add-plan-item-dropdown">
                                            <i className="fas fa-plus"></i>
                                        </Dropdown.Toggle>
                                        <Dropdown.Menu>
                                            <Dropdown.Item onClick={() => handleOpenNewItemModal('header')}>Заголовок</Dropdown.Item>
                                            <Dropdown.Item onClick={() => handleOpenNewItemModal('item')}>Пункт плана</Dropdown.Item>
                                            <Dropdown.Item onClick={() => handleOpenNewItemModal('song')}>Песня</Dropdown.Item>
                                        </Dropdown.Menu>
                                    </Dropdown>
                                )}
                            </div>
                        </div>
                        <div className="card-body">
                            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                                <table className="table">
                                    <thead><tr>{canUpdate && <th style={{ width: '1rem' }}></th>}<th className="d-none d-md-table-cell">Время</th><th>Описание</th><th className="d-none d-md-table-cell">Комментарий</th><th className="d-none d-md-table-cell">Ведущий</th>{canUpdate && <th className="text-end">...</th>}</tr></thead>
                                    <SortableContext items={event.plan_items.map(item => item.id)} strategy={verticalListSortingStrategy}>
                                        <tbody>
                                            {event.plan_items.map(item => <SortableEventPlanItem key={item.id} item={item} responsible={item.responsible} formatDuration={formatDuration} handleOpenEditItemModal={handleOpenEditItemModal} handleDeleteItemClick={handleDeleteItemClick} canEditEvent={canUpdate} />)}
                                        </tbody>
                                    </SortableContext>
                                </table>
                            </DndContext>
                        </div>
                    </div>
                </Tab>
                <Tab eventKey="team" title="Команда">
                    <div className="card">
                        <div className="card-header d-flex justify-content-between align-items-center">
                            <h2 className="h4 mb-0">Команда</h2>
                            {canUpdate && <button className="btn btn-primary btn-sm" onClick={handleOpenAddParticipantModal} title="Добавить участника"><i className="fas fa-plus"></i></button>}
                        </div>
                        <div className="card-body">
                            {(() => {
                                const sortedParticipants = [...event.participants].sort((a, b) => {
                                    const statusOrder = { 'unconfirmed': 0, 'confirmed': 1 };
                                    const statusA = statusOrder[a.status] ?? 99;
                                    const statusB = statusOrder[b.status] ?? 99;
                                    if (statusA !== statusB) {
                                        return statusA - statusB;
                                    }
                                    const nameA = a.user.name || a.user.email;
                                    const nameB = b.user.name || b.user.email;
                                    return nameA.localeCompare(nameB);
                                });

                                if (sortedParticipants.length === 0) {
                                    return <p className="mb-0">В команде пока никого нет.</p>;
                                }

                                return (
                                    <table className="table">
                                        <thead><tr><th style={{ width: '100%' }}>Участник</th><th className="text-end">Статус</th>{canUpdate && <th className="text-end">...</th>}</tr></thead>
                                        <tbody>
                                            {sortedParticipants.map(p => (
                                                <tr key={p.id}>
                                                    <td>
                                                        <div>{p.user.name || p.user.email}</div>
                                                        <small className="text-muted">{p.role.name}</small>
                                                    </td>
                                                    <td className="text-end">
                                                        {p.status === 'confirmed' ? 
                                                            <i className="fas fa-check-circle text-success" title="Подтверждено"></i> :
                                                            (p.user.id === user.id ?
                                                                <button className="btn btn-success btn-sm" onClick={() => handleConfirmParticipation(p.id)}>
                                                                    Подтвердить
                                                                </button> :
                                                                <i className="fas fa-clock text-warning" title="Ожидает подтверждения"></i>
                                                            )
                                                        }
                                                    </td>
                                                    {canUpdate && <td className="text-end"><Dropdown><Dropdown.Toggle as={CustomToggle}><i className="fas fa-ellipsis-v"></i></Dropdown.Toggle><Dropdown.Menu><Dropdown.Item onClick={() => handleResendInvitation(p.id)}>Отправить приглашение</Dropdown.Item><Dropdown.Item onClick={() => handleDeleteParticipantClick(p.id)} className="text-danger">Удалить</Dropdown.Item></Dropdown.Menu></Dropdown></td>}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                );
                            })()}
                        </div>
                    </div>
                </Tab>
                <Tab eventKey="rehearsals" title="Репетиции">
                    <div className="card">
                        <div className="card-header d-flex justify-content-between align-items-center">
                            <h2 className="h4 mb-0">Репетиции</h2>
                            {canUpdate && <button className="btn btn-primary btn-sm" onClick={handleOpenAddRehearsalModal} title="Добавить репетицию"><i className="fas fa-plus"></i></button>}
                        </div>
                        <div className="card-body">
                            {event.rehearsals?.length > 0 ? (
                                <ul className="list-group">
                                    {event.rehearsals.sort((a, b) => new Date(a.date) - new Date(b.date)).map(r => (
                                        <li key={r.id} className="list-group-item d-flex justify-content-between align-items-center">
                                            {formatUtcToLocalWithWeekday(r.date)}
                                            {canUpdate && <Dropdown><Dropdown.Toggle as={CustomToggle}><i className="fas fa-ellipsis-v"></i></Dropdown.Toggle><Dropdown.Menu><Dropdown.Item onClick={() => handleDeleteRehearsalClick(r.id)} className="text-danger">Удалить</Dropdown.Item></Dropdown.Menu></Dropdown>}
                                        </li>
                                    ))}
                                </ul>
                            ) : <p className="mb-0">Репетиций пока не запланировано.</p>}
                        </div>
                    </div>
                </Tab>
            </Tabs>

            <EventPlanItemModal show={showItemModal} handleClose={handleCloseItemModal} onSave={handleSaveItem} event={event} item={editingItem} responsibleSuggestions={responsibleSuggestions} modalType={itemModalType} />
            <AddParticipantModal show={showParticipantModal} onHide={handleCloseAddParticipantModal} users={users} eventRoles={eventRoles} onSave={handleAddParticipant} currentUser={user} eventDate={event.date} participants={event.participants} />
            <PlaylistPlayerModal show={showPlayerModal} onHide={() => setShowPlayerModal(false)} playlist={playlist} eventName={event.name} />
            <ConfirmationModal show={showConfirmDeleteModal} title="Подтвердить удаление" message="Вы уверены, что хотите удалить этот пункт плана?" onConfirm={handleConfirmDelete} onCancel={() => setShowConfirmDeleteModal(false)} confirmText="Удалить" cancelText="Отмена" />
            <ConfirmationModal show={showConfirmDeleteParticipantModal} title="Подтвердить удаление" message="Вы уверены, что хотите удалить этого участника?" onConfirm={handleConfirmDeleteParticipant} onCancel={() => setShowConfirmDeleteParticipantModal(false)} confirmText="Удалить" cancelText="Отмена" />
            <AddRehearsalModal show={showAddRehearsalModal} onHide={handleCloseAddRehearsalModal} onSave={handleSaveRehearsal} />
            <ConfirmationModal show={showConfirmDeleteRehearsalModal} title="Подтвердить удаление" message="Вы уверены, что хотите удалить эту репетицию?" onConfirm={handleConfirmDeleteRehearsal} onCancel={() => setShowConfirmDeleteRehearsalModal(false)} confirmText="Удалить" cancelText="Отмена" />
            <EditEventModal show={showEditModal} onHide={() => setShowEditModal(false)} event={event} onSave={handleSaveEvent} rehearsals={event.rehearsals} />
            <ScaleModal
                show={showScaleModal}
                title="Масштаб PDF"
                message="Выберите масштаб для генерации PDF документа."
                onConfirm={handleConfirmDownloadPdf}
                onCancel={() => setShowScaleModal(false)}
                confirmText="Сгенерировать"
                cancelText="Отмена"
            />
        </div>
    );
};

export default EventPage;
